/**
 * The name of the native browser `CustomEvent` dispatched on `window` whenever a
 * custom-choice dialog should be shown.
 *
 * Like {@link ./confirmation-dialog-event.ts}, using a native browser event (instead of a redux
 * action) keeps the producer fully decoupled from React and redux: any code — including non-React
 * code, redux epics, or services — can request a dialog with an arbitrary set of choices without
 * depending on the modal context. A listener mounted inside `ModalDialogProvider` turns the event
 * into a rendered dialog and settles the promise returned by {@link dispatchCustomChoiceDialog}
 * with the value of the choice the user picked.
 */
export const customChoiceDialogEventName = 'famly/custom-choice-dialog';
/**
 * Dispatches a custom-choice dialog request as a native browser `CustomEvent` on `window`
 * and resolves once the user picks one of the choices.
 *
 * A listener mounted inside `ModalDialogProvider` renders the dialog and settles the returned
 * promise with the picked choice's `value`. If no provider is mounted, the promise never settles.
 *
 * @param options the custom-choice dialog to show
 * @returns a promise resolving to the `value` of the choice the user picked
 *
 * @example
 * const choice = await dispatchCustomChoiceDialog({
 *     title: i18next.t($ => $.calendar.event.changingRepeatingEvent),
 *     description: i18next.t($ => $.calendar.event.changingRepeatingEventDescription),
 *     choices: [
 *         {text: i18next.t($ => $.cancel), variant: 'secondary', value: 'cancel'},
 *         {text: i18next.t($ => $.calendar.event.onlyThis), variant: 'secondary', value: 'only-this'},
 *         {text: i18next.t($ => $.calendar.event.allFuture), variant: 'primary', value: 'all-future'},
 *     ],
 * });
 * if (choice === 'all-future') {
 *     // perform the action
 * }
 */
export function dispatchCustomChoiceDialog(options) {
    return new Promise(resolve => {
        window.dispatchEvent(new CustomEvent(customChoiceDialogEventName, {
            detail: { ...options, resolve: resolve },
        }));
    });
}
